import IconButton from '@betterfunnel/ui-core/IconButton';
import Tooltip from '@betterfunnel/ui-core/Tooltip';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { MinusIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Table } from '@tanstack/react-table';
import { capitalize } from '../../utils';
import SortableList from '../SortableList';

interface Props {
  table: Table<any>;
  open: boolean;
  onClose: () => void;
}

export default function ColumnManagementDrawer({
  table,
  open,
  onClose,
}: Props) {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-10">
      <div className="fixed inset-0" />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-0 sm:pl-16">
            <DialogPanel
              transition
              className="pointer-events-auto h-screen w-screen max-w-2xl transform transition duration-500 ease-in-out data-closed:translate-x-full sm:duration-700"
            >
              <form className="flex h-full flex-col overflow-hidden bg-white shadow-xl">
                <div className="flex flex-1 flex-col overflow-hidden">
                  {/* Header */}
                  <div className="bg-gray-50 px-4 py-6 sm:px-6">
                    <div className="flex items-start justify-between space-x-3">
                      <div className="flex-1 space-y-1">
                        <DialogTitle className="text-base font-semibold text-gray-900">
                          Manage Columns
                        </DialogTitle>
                        {/* <p className="text-sm text-gray-500">
                          Publish a new version of the dynamic page to make it
                          live.
                        </p> */}
                      </div>
                      <div className="flex h-7 items-center">
                        <IconButton variant="ternary" onClick={() => onClose()}>
                          <XMarkIcon aria-hidden="true" className="size-6" />
                        </IconButton>
                      </div>
                    </div>
                  </div>

                  {/* Divider container */}
                  <div className="block flex-1 overflow-scroll py-6 sm:py-0">
                    <div className="relative flex h-full w-full flex-col">
                      <SortableList
                        itemClassName="py-4 px-4 hover:bg-gray-50"
                        items={table
                          .getAllLeafColumns()
                          .filter((column) => column.getIsVisible())}
                        renderItemContent={(column) => {
                          return (
                            <div className="flex w-full items-center justify-between">
                              <span className="flex-1 truncate">
                                {(typeof column.columnDef.header === 'string'
                                  ? column.columnDef.header
                                  : undefined) ||
                                  capitalize(column.columnDef.id || '') ||
                                  'Unknown'}
                              </span>
                              <Tooltip text="Hide column">
                                <IconButton
                                  variant="ternary"
                                  onClick={() => {
                                    column.toggleVisibility(false);
                                  }}
                                >
                                  <MinusIcon className="size-4" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          );
                        }}
                        onChange={(items) => {
                          table.setColumnOrder(items.map((item) => item.id));
                        }}
                        disabledIds={table
                          .getAllFlatColumns()
                          .filter((column) => column.getIsPinned())
                          .map((column) => column.id)}
                      />
                      {!!table
                        .getAllLeafColumns()
                        .filter((column) => !column.getIsVisible())?.length && (
                        <>
                          <div className="relative my-4">
                            <div
                              className="absolute inset-0 flex items-center"
                              aria-hidden="true"
                            >
                              <div className="w-full border-t border-gray-200" />
                            </div>
                            <div className="relative flex justify-center">
                              <span className="bg-white px-4 text-sm text-gray-900">
                                More Columns
                              </span>
                            </div>
                          </div>

                          <SortableList
                            itemClassName="py-4 px-4 hover:bg-gray-50"
                            items={table
                              .getAllLeafColumns()
                              .filter((column) => !column.getIsVisible())}
                            renderItemContent={(column) => {
                              return (
                                <div className="flex w-full items-center justify-between">
                                  <span className="flex-1 truncate">
                                    {(typeof column.columnDef.header ===
                                    'string'
                                      ? column.columnDef.header
                                      : undefined) ||
                                      capitalize(column.columnDef.id || '') ||
                                      'Unknown'}
                                  </span>
                                  <IconButton
                                    variant="ternary"
                                    onClick={() => {
                                      column.toggleVisibility(true);
                                    }}
                                  >
                                    <PlusIcon className="size-4" />
                                  </IconButton>
                                </div>
                              );
                            }}
                            onChange={(items) => {
                              table.setColumnOrder(
                                items.map((item) => item.id),
                              );
                            }}
                            disabledIds={table
                              .getAllLeafColumns()
                              .filter((column) => !column.getIsVisible())
                              .map((column) => column.id)}
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                {/* <div className="shrink-0 border-t border-gray-200 px-4 py-5 sm:px-6">
                  <div className="flex justify-end space-x-3">
                    <Button variant="secondary" onClick={() => onClose()}>
                      Cancel
                    </Button>
                    <Button
                      loading={isLoadingPublish}
                      variant="primary"
                      disabled={isPublishButtonDusabled}
                      onClick={onPublishVersionButtonClick}
                    >
                      Publish
                    </Button>
                  </div>
                </div> */}
              </form>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
